import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { deleteUsers, getAllUsers } from "../../api/Users";
import "../../vendor/css/viewusers.css";

const ViewUsers = () => {
  const [usersList, setUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  useEffect(() => {
    const getUsersList = async () => {
      try {
        const response = await getAllUsers();
        setUsersList(response?.users || response || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        Swal.fire("Error", "Failed to load users", "error");
      } finally {
        setLoading(false);
      }
    };
    getUsersList();
    const interval = setInterval(getUsersList, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const handleDeleteUsers = (userToDelete) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteUsers(userToDelete)
          .then((res) => {
            if (res.status === 200) {
              Swal.fire("Deleted!", "Your file has been deleted.", "success");
              setUsersList((prev) =>
                prev.filter((item) => item._id !== userToDelete._id)
              );
            }
          })
          .catch((err) => {
            console.error("Delete failed:", err);
            Swal.fire("Error", "Failed to delete destination", "error");
          });
      }
    });
  };

  // Filter users based on search term
  const filteredUsers = usersList.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone_no?.includes(searchTerm)
  );

  // Pagination logic
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="view-users">
        <div className="users-header">
          <h1>Users Management</h1>
          <p>Manage and view all registered users</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="view-users">
      <div className="users-header">
        <h1 style={{ fontFamily: 'serif' }}>Users Management</h1>
        <p style={{ fontFamily: 'serif' }}>Manage and view all registered users</p>
      </div>

      <div className="users-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="bx bx-user"></i>
          </div>
          <div className="stat-content">
            <h3>{usersList.length}</h3>
            <p>Total Users</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <i className="bx bx-search"></i>
          </div>
          <div className="stat-content">
            <h3>{filteredUsers.length}</h3>
            <p>Filtered Results</p>
          </div>
        </div>
      </div>

      <div className="users-controls">
        <div className="search-container">
          <i className="bx bx-search search-icon"></i>
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact Information</th>
              <th>Address</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map((user) => (
                <tr key={user._id}>
                  <td>
                    <div className="user-name">
                      <div className="avatar">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <span>{user.name}</span>
                    </div>
                  </td>
                  <td>
                    <div className="contact-info">
                      <a href={`tel:${user.phone_no}`} className="contact-link">
                        <i className="bx bx-phone"></i>
                        {user.phone_no}
                      </a>
                      <a href={`mailto:${user.email}`} className="contact-link">
                        <i className="bx bx-envelope"></i>
                        {user.email}
                      </a>
                    </div>
                  </td>
                  <td>
                    <span className="address">{user.address || 'N/A'}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-danger delete-btn"
                        onClick={() => handleDeleteUsers(user)}
                        title="Delete User"
                      >
                        <i className="bx bx-trash"></i>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="no-data">
                  <div className="no-data-content">
                    <i className="bx bx-user-x"></i>
                    <p>No users found</p>
                    <small>Try adjusting your search criteria</small>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            <i className="bx bx-chevron-left"></i>
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => paginate(i + 1)}
              className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            <i className="bx bx-chevron-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewUsers;
